<template>
  <v-form ref="form" v-model="valid" autocomplete="off" @submit.prevent="createNewScenario">
    <v-container max-width="none">
      <v-row v-if="isOpen" class="form-body">
        <v-col>
          <div class="form-container">
            <AssortmentTextField
              :key="nameKey"
              v-model="scenarioName"
              :rules="rules"
              :placeholder="$t('scenarios.scenarioName')"
              hide-details
              bg-color="white"
              variant="filled"
              tooltip-location="bottom"
            />
            <v-btn
              save
              class="float-right px-5 ml-6"
              :disabled="!valid"
              :loading="isNewScenarioRunning"
              type="submit"
            >
              {{ $t('actions.save') }}
            </v-btn>
          </div>
        </v-col>
        <v-spacer />
        <v-spacer />
      </v-row>
      <v-row class="mt-3 mb-0">
        <v-btn
          id="button-add"
          :disabled="isOpen || !canCreateScenario"
          primary
          @click="isOpen = true"
        >
          {{ $t('scenarios.newScenario') }}
        </v-btn>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapState, mapActions } from 'pinia';
import scenarioErrors from '@enums/scenario-errors';
import { useInputValidations } from '@/composables/input-validations';
import { usePermission } from '@/composables/use-permission';
import { useScenariosStore } from '@/stores/scenarios';

export default {
  props: { workpackageId: { required: true, type: String } },

  setup() {
    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    const { canCreateScenario } = usePermission();
    return {
      canCreateScenario,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    };
  },
  data() {
    return {
      valid: true,
      isOpen: false,
      isNewScenarioRunning: false,
      nameKey: false,
      scenarioName: '',
      scenarioNameServerError: '',
      rules: [this.required, this.isUniqueScenarioName],
    };
  },

  computed: { ...mapState(useScenariosStore, ['scenarios']) },

  async created() {
    await this.fetchWpScenarios();
  },

  methods: {
    ...mapActions(useScenariosStore, ['createScenario', 'fetchScenarios']),

    async fetchWpScenarios() {
      this.nameKey = !this.nameKey; // force rtls-text-field to re-render with new prop value
      await this.fetchScenarios({ params: { where: { workpackageId: this.workpackageId } } });
    },

    async createNewScenario() {
      this.isNewScenarioRunning = true;
      const scenario = {
        name: this.scenarioName.trim(),
        workpackageId: this.workpackageId,
      };
      const { data, errors } = await this.createScenario({ scenario });
      this.$emit('save:scenario');

      if (
        !isEmpty(errors) &&
        errors.filter(e => e.code === scenarioErrors.Errors.ScenarioNotUnique).length
      ) {
        this.scenarioNameServerError = this.$t('validationErrors.unique', [
          this.$t('scenarios.scenarioName'),
        ]);
      }
      // if scenario was created (data object is not empty) reset data to defaults
      if (!isEmpty(data)) {
        this.isOpen = false;
        this.scenarioName = '';
      }

      this.isNewScenarioRunning = false;
      await this.fetchWpScenarios();
    },

    isUniqueScenarioName(value) {
      if (this.scenarios.filter(sc => sc.name === value.trim()).length) {
        return this.$t('validationErrors.unique', [this.$t('scenarios.scenarioName')]);
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

form {
  background-color: $assortment-background;
}

.v-container {
  background-color: $assortment-background;
  padding: 5px 0 10px 0;
  margin: 0;

  .v-col {
    padding-left: 6px;
  }

  .form-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .v-btn {
      margin-left: 20px;
    }

    :deep(.error-triangle-container) {
      padding-left: 5px;
    }
  }

  .form-body {
    border-top: 1px solid $assortment-secondary-colour;
    border-bottom: 1px solid $assortment-secondary-colour;
    margin-bottom: 10px;

    //deep selector to select deep nested elements within v-input class
    :deep(.v-input) {
      width: 248px;
      padding: 0px;
      margin: 0px;
      input,
      input {
        max-height: 28px;
        padding-top: 9px;
      }
      .v-input__slot {
        margin-bottom: 0px;
      }
      .v-text-field__slot {
        background-color: white;
      }
      .v-input__append {
        margin-left: 8px;
      }
    }
  }
}
</style>
